<template>
  <ion-page>
    <main-header :back-href="backHref"></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <form @submit.prevent="handleContinueToCheckout()" v-else>
        <ion-list data-cy="review-and-confirm-list">
          <ion-item
            v-for="(lineItem, index) in orderItemsWithQuantity"
            :key="`item-${index}`"
            :data-cy="`review-item-${lineItem.id}`"
          >
            <ion-select
              slot="start"
              @ionChange="changeQuantity(index, $event.detail.value)"
              :value="lineItem.quantity"
            >
              <ion-select-option value="0">0</ion-select-option>
              <ion-select-option value="1">1</ion-select-option>
              <ion-select-option value="2">2</ion-select-option>
              <ion-select-option value="3">3</ion-select-option>
              <ion-select-option value="4">4</ion-select-option>
            </ion-select>
            <ion-label>
              {{ menuItem(lineItem.id).name }}
              <span v-if="lineItem.optionValueId">
                - {{ optionValue(lineItem.id, lineItem.optionValueId).name }}
              </span>
              <p v-if="lineItem.instructions">{{ lineItem.instructions }}</p>
            </ion-label>
            {{ priceQuantity(menuItem(lineItem.id), lineItem) | usd }}
          </ion-item>
          <ion-item>
            <ion-label>
              <p>Service Charge</p>
            </ion-label>
            {{ serviceCharge | usd }}
          </ion-item>
          <ion-item>
            <ion-label>Total</ion-label>
            <strong>{{ orderTotal | usd }}</strong>
          </ion-item>
        </ion-list>
        <div class="ion-padding">
          <p class="alcohol-warning">
            * MUST BE 21 OR OLDER TO PURCHASE ALCOHOL
          </p>
          <submit-button
            :submitting="submitting"
            data-cy="continue-to-checkout-button"
          >
            Continue to Checkout
          </submit-button>
        </div>
      </form>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import errorAlert from "@/ErrorAlert";
import Menu from "@/models/Menu";
import submittable from "@/mixins/submittable";

let menuItemsCache = {};
let optionValueCache = {};

export default {
  title: "Review & Confirm",

  mixins: [submittable],

  data: function() {
    return {
      loading: true,
      menu: null
    };
  },

  /**
   * Why am I re-loading the menu? Well this is the last time we have access to a REAL
   * menu before it gets written. So, in the rare case where a person adds something to
   * their cart - and then it gets changed, and then they get here, we want to show the
   * newest possible stuff
   */
  created() {
    this.$firestore()
      .collection("menus")
      .withConverter(Menu.firebaseConverter)
      .doc(this.location.id)
      .get()
      .then(querySnapshot => {
        this.menu = querySnapshot.data();
      })
      .catch(e => {
        errorAlert(e.message);
      })
      .finally(() => {
        this.loading = false;
      });
  },

  computed: {
    ...mapGetters("order", {
      orderItems: "orderItems",
      location: "orderLocation"
    }),

    serviceCharge() {
      return this.location.serviceCharge;
    },

    orderItemsWithQuantity() {
      return this.orderItems.filter(orderItem => orderItem.quantity > 0);
    },

    orderTotal() {
      return (
        this.orderItems.reduce((total, orderItem) => {
          return (
            total + this.priceQuantity(this.menuItem(orderItem.id), orderItem)
          );
        }, 0) + this.serviceCharge
      );
    },

    backHref() {
      return this.$router.resolve({
        name: "App: Location Menu"
      }).href;
    }
  },

  methods: {
    ...mapMutations("order", ["updateQuantity"]),

    handleContinueToCheckout() {
      this.$router.push({ name: "App: Order Payment" }).catch(e => e);
    },

    menuItem(id) {
      if (!menuItemsCache[id]) {
        let topLevelItems = this.menu.categories.reduce((items, category) => {
          if (category.items) {
            return items.concat(category.items);
          } else {
            return items;
          }
        }, []);

        let result = topLevelItems.filter(item => item.id == id);

        if (result.length) {
          menuItemsCache[id] = result[0];
        } else {
          // highly unlikely this would ever happen, but doing this so it doesn't crash
          menuItemsCache[id] = {
            name: "Not Available",
            price: 0
          };
        }
      }

      return menuItemsCache[id];
    },

    optionValue(menuId, optionValueId) {
      if (!optionValueCache[optionValueId]) {
        let menuItem = this.menuItem(menuId);

        if (!menuItem.options) {
          // again, highly unlikely, but being defensive
          optionValueCache[optionValueId] = {
            name: "No Options Available",
            price: 0
          };
        } else {
          let result = menuItem.options.values.filter(
            value => value.id == optionValueId
          );

          if (result.length) {
            optionValueCache[optionValueId] = result[0];
          } else {
            // again, unlikely
            optionValueCache[optionValueId] = {
              name: "Not Available",
              price: 0
            };
          }
        }
      }

      return optionValueCache[optionValueId];
    },

    priceQuantity(menuItem, lineItem) {
      let price = 0;
      if (menuItem.price) {
        price = menuItem.price;
      } else {
        price = this.optionValue(menuItem.id, lineItem.optionValueId).price;
      }

      return price * lineItem.quantity;
    },

    changeQuantity(index, quantity) {
      this.updateQuantity({ index, quantity });

      if (!this.orderItems.length) {
        this.$router.push({ name: "App: Location Menu" }).catch(e => e);
      }
    }
  }
};
</script>

<style lang="scss">
.alcohol-warning {
  text-align: center;
  font-size: 14px;
}
</style>
